import React from "react";
import RightworkSelect from "../../../components/shared/RightworkSelect";
import { API_google_hours_audit } from "../../../constants";
import { postRightworkAPI } from "../requests/utilities/requests";

const days = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
const time = value => {
  const minutes = Math.round((value % 24) * 60);
  const hour = Math.floor(minutes / 60);
  return `${hour % 12 || 12}:${String(minutes % 60).padStart(2, "0")}${hour < 12 ? "am" : "pm"}`;
};
const hours = row => row.open_time == null ? "Not set" : row.open_time === row.close_time ? "Closed" : `${time(row.open_time)}–${time(row.close_time)}`;
const outline = "rounded-xl border border-slate-200 bg-white px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50";

export default function GoogleHoursAudit({ locations = [], onSaved }) {
  const [saveScope, setSaveScope] = React.useState("from_today");
  const [results, setResults] = React.useState([]);
  const [busy, setBusy] = React.useState(false);
  const [saving, setSaving] = React.useState(false);
  const [message, setMessage] = React.useState("");
  const [error, setError] = React.useState("");
  const generation = React.useRef(0);
  React.useEffect(() => () => { generation.current += 1; }, []);
  const check = async () => {
    const run = ++generation.current;
    setBusy(true); setResults([]); setMessage(""); setError("");
    let next = 0;
    const worker = async () => {
      while (next < locations.length && generation.current === run) {
        const location = locations[next++];
        let result;
        try {
          const response = await postRightworkAPI(API_google_hours_audit, { action: "lookup", store_id: location.storeId, save_scope: saveScope });
          result = response.data;
        } catch (err) {
          result = { status: "unavailable", message: err.response?.data?.message || "Google check unavailable. Try again later." };
        }
        if (generation.current === run) setResults(previous => [...previous, { ...result, ...location, selected: result.status === "different" }]);
      }
    };
    await Promise.all([worker(), worker()]);
    if (generation.current === run) setBusy(false);
  };
  const save = async () => {
    setSaving(true); setError("");
    try {
      const response = await postRightworkAPI(API_google_hours_audit, { action: "apply", proposals: results.filter(row => row.selected).map(row => row.proposal) });
      setMessage(`Updated ${response.data.saved} locations. ${saveScope === "historical_correction" ? "Historical weekly hours were corrected." : "Hours apply from today; historical hours were preserved."} Date-specific exceptions are unchanged.`);
      setResults([]);
      await onSaved?.();
    } catch (err) { setError(err.response?.data?.message || "Unable to save. Check the hours again and retry."); }
    finally { setSaving(false); }
  };
  if (!locations.length) return null;
  const differences = results.filter(row => row.status === "different");
  return <section className="mb-5 rounded-2xl border border-slate-200 bg-white p-5" aria-label="Google hours check">
    <div className="flex flex-wrap items-center justify-between gap-3">
      <div><h3 className="text-sm font-semibold text-slate-900">Verify operating hours</h3>
        <p className="mt-1 text-sm text-slate-500">Use Google’s address and hours when available, with official websites as a fallback. Review changes before saving.</p></div>
      <button className={outline} disabled={busy || saving} onClick={check}>{busy ? `Checking ${results.length} of ${locations.length}…` : "Check hours on Google"}</button>
    </div>
    <div className="mt-3 max-w-md">
      <label id="hours-audit-scope-label" htmlFor="hours-audit-scope" className="mb-1 block text-sm text-slate-700">Apply verified hours</label>
      <RightworkSelect id="hours-audit-scope" value={saveScope} disabled={busy || saving}
        onChange={event => { setSaveScope(event.target.value); setResults([]); setMessage(""); }}
        options={[{ value: "from_today", label: "From today — preserve historical hours" },
          { value: "historical_correction", label: "Correct historical weekly hours" }]} />
    </div>
    {message && <p role="status" className="mt-3 text-sm text-emerald-700">{message}</p>}
    {error && <p role="alert" className="mt-3 text-sm text-rose-700">{error}</p>}
    {!!results.length && <div className="mt-4 space-y-3">
      <p role="status" className="text-sm text-slate-500">{results.filter(row => row.status === "match").length} match · {differences.length} with differences · {results.filter(row => row.status === "unavailable").length} couldn’t be verified</p>
      {results.filter(row => row.status !== "match").map(row => <div key={row.storeId} className="border-t border-slate-100 pt-3">
        <label className="flex items-center gap-3 text-sm font-medium text-slate-900">
          {row.status === "different" && <input type="checkbox" className="accent-violet-700" checked={row.selected} disabled={saving} onChange={() => setResults(previous => previous.map(item => item.storeId === row.storeId ? { ...item, selected: !item.selected } : item))} />}
          {row.locationName}
        </label>
        {row.message && <p className="mt-1 text-sm text-slate-500">{row.message}</p>}
        {row.address_change && <div className="mt-2 rounded-xl bg-violet-50 p-3 text-sm text-slate-700">
          <p className="font-medium">Confirm Google’s address for this location</p>
          <p className="mt-1">Saved: {row.address_change.before}</p>
          <p className="mt-1">Google: {row.address_change.after}</p>
          <p className="mt-1 text-xs text-slate-500">Saving updates this address and any hours shown below.</p>
        </div>}
        {row.changes?.map(change => <p key={change.dow} className="mt-1 text-sm text-slate-600">{days[change.dow - 1]}: {hours(change.before)} → {hours(change.after)}</p>)}
        {row.sources?.map(url => <a key={url} href={url} target="_blank" rel="noreferrer" className="mr-3 text-sm text-violet-700 underline">{row.source_type === "official_website" ? "Official website" : "Google listing"}</a>)}
      </div>)}
      {!!differences.length && <div className="flex flex-wrap items-center justify-between gap-3 border-t border-slate-100 pt-4">
        <p className="text-xs text-slate-500">{saveScope === "historical_correction" ? "Replaces past and future weekly hours, including scheduled weekly changes. Only use this to correct historical data." : "Applies from today. Historical hours and later scheduled changes stay in place."} Date-specific exceptions stay in place.</p>
        <button className="rounded-xl bg-violet-700 px-4 py-2 text-sm font-medium text-white hover:bg-violet-800 disabled:opacity-50" disabled={busy || saving || !results.some(row => row.selected)} onClick={save}>{saving ? "Saving…" : "Save selected changes"}</button>
      </div>}
    </div>}
  </section>;
}
